import Vue from "vue";
import axios from "axios";
import {
  PARAM_USER_CREATED,
  PARAM_RESOURCE_INFO_KEY,
  PARAM_BACKGROUND_KEY
} from "../constants/url-params";
import { FORM_TYPE } from "../constants/custom-define-type";
import { appendUrlParam, appendBackgroundParam, appendHideLoadingParam } from "../utils/query-handling";

Vue.mixin({
  data() {
    return {
      appendUrlParam,
      appendBackgroundParam,
      appendHideLoadingParam
    }
  },
  computed: {
    rootUrl() {
      if (window.location.origin) {
        return window.location.origin;
      } else {
        return (
          window.location.protocol +
          "//" +
          window.location.hostname +
          (window.location.port ? ":" + window.location.port : "")
        );
      }
    },
  },
  methods: {
    // Append query parameter at URL to use when handling error
    appendWarningUserCreated(apiPath, resource) {
      if (apiPath.includes("?")) {
        return `${apiPath}&${PARAM_USER_CREATED}&${PARAM_RESOURCE_INFO_KEY}=${resource}`;
      } else {
        return `${apiPath}?${PARAM_USER_CREATED}&${PARAM_RESOURCE_INFO_KEY}=${resource}`;
      }
    },
    // Get value of a query parameter by name
    // Example: URL: login?user=test
    // Name: user
    // Output: test
    getParameterByName(url, name) {
      name = name.replace(/[\[\]]/g, "\\$&");
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
        results = regex.exec(url);
      if (!results) {
        return null;
      }
      if (!results[2]) {
        return null;
      }
      return decodeURIComponent(results[2].replace(/\+/g, " ").replace("/", ""));
    },
    /*
    Send patch request to patchUrl (example: /api/people/)
    listFields may contains both file and non-file field
    Therefore, it need to separate into 2 request: multipart-form request for file and normal json request for non-file data
    */
    patchQueryForListFields(patchUrl, applicationData, listFields, appendData) {
      return new Promise(async (resolve, reject) => {
        // Find all model fields
        const listFileModels = listFields
          .filter(field => field.type === FORM_TYPE.FILE)
          .map(field => field.model);

        // Separate file data and non file data
        let formData = new FormData();
        const jsonData = {};
        let hasFileData = false;
        let hasNonFileData = false;
        for (const model of Object.keys(applicationData)) {
          // Separate file and non-file field
          if (listFileModels.includes(model)) {
            if ((applicationData[model] != null) && (typeof applicationData[model] === "object")) {
              hasFileData = true;
              formData.append(model, applicationData[model]);
            }
          } else {
            hasNonFileData = true;
            // Check timepicker field
            const field = listFields.find(field => field.model === model);
            if (field && field.type === FORM_TYPE.TIME_PICKER) {
              jsonData[model] = this.getTimeString(applicationData[model]);
            } else {
              jsonData[model] = applicationData[model];
            }
          }
        }
        let res;
        // Patch non file data
        if (hasNonFileData || appendData) {
          res = await axios.patch(patchUrl, { ...jsonData, ...appendData });
          if (res == null) {
            reject();
          }
        }

        // Patch files
        if (hasFileData) {
          res = await axios.patch(patchUrl, formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          });
          if (res == null) {
            reject();
          }
        }
        resolve(res);
      });
    },
    /*
    Send post request to create object, for example: children, pet, ...
    If there is a file (in case of profile image for pet), send post request to create object,
    then send patch request to submit file
    extraData: info send with the object, for example: agencyId, agency name, object type, ...
    */
    postQueryForListFields(url, resourceParm, applicationData, listFields, extraData) {
      return new Promise(async (resolve, reject) => {
        // Find all model fields
        const listFileModels = listFields
          .filter(field => field.type === FORM_TYPE.FILE)
          .map(field => field.model);

        // Separate file data and non file data
        let formData = new FormData();
        const jsonData = {};
        let hasFileData = false;
        for (const model of Object.keys(applicationData)) {
          if (listFileModels.includes(model)) {
            if (applicationData[model] != null) {
              hasFileData = true;
              formData.append(model, applicationData[model]);
            }
          } else {
            // Check timepicker field
            const field = listFields.find(field => field.model === model);
            if (field && field.type === FORM_TYPE.TIME_PICKER) {
              jsonData[model] = this.getTimeString(applicationData[model]);
            } else {
              jsonData[model] = applicationData[model];
            }
          }
        }

        // Create object
        const postURL = this.appendWarningUserCreated(url,
          resourceParm
        );
        let res = await axios.post(postURL, { ...jsonData, ...extraData });
        if (res == null) {
          reject();
        }
        let id;
        if (Array.isArray(res.data)) {
          id = res.data[0].id;
        } else if (res.data) {
          id = res.data.id;
        }

        // Patch files
        if (hasFileData) {
          const patchURL = this.appendWarningUserCreated(`${url}${id}`,
            resourceParm
          );
          res = await axios.patch(patchURL, formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          });
          if (res == null) {
            reject();
          }
        }
        resolve(id);
      });
    },
    getEmailTemplates(url) {
      return new Promise(async (resolve, reject) => {
        const res = await axios.get(url);
        if (!res || !res.data) {
          reject();
          return;
        }
        let listEmailTemplates = res.data;
        if (this.$store.getters.agent && this.$store.getters.agent.id) {
          listEmailTemplates = listEmailTemplates.filter(
            (template) =>
              !template.agent_users ||
              template.agent_users.length === 0 ||
              template.agent_users.includes(this.$store.getters.agent.id)
          );
        }
        resolve({
          data: listEmailTemplates,
        });
      });
    },
    getAdminSetting(reload = false, userID = null) {
      if (!userID) {
        userID = this.$store.getters.userID;
      }
      return new Promise(async (resolve) => {
        if (!userID) {
          resolve(null);
          return;
        }
        if (!this.$store.getters.isAgent) {
          resolve(null);
          return;
        }
        // Load old data
        if (
          !reload &&
          this.$store.getters.adminSetting &&
          Object.keys(this.$store.getters.adminSetting).length
        ) {
          resolve(this.$store.getters.adminSetting);
          return;
        }
        const res = await axios.get(
          `/api/agentsettings/?agent=${userID}`
        );
        if (res && res.data) {
          const listAgentSetting = res.data.sort((a, b) => (a.id > b.id ? 1 : -1));
          console.log(listAgentSetting)
          if (listAgentSetting.length > 0) {
            const emailFooter = listAgentSetting[0].email_footer;
            const smsFooter = listAgentSetting[0].sms_footer;
            const id = listAgentSetting[0].id;
            const notificationScope = listAgentSetting[0].notification_scope;
            const adminSetting = {
              ...listAgentSetting[0], emailFooter: emailFooter,
              smsFooter: smsFooter, notificationScope: notificationScope
            };
            console.log(adminSetting);
            this.$store.dispatch('setAdminSetting', adminSetting);
            resolve(adminSetting);
            return;
          } else {
            this.$store.dispatch('setAdminSetting', {});
            resolve(null);
            return;
          }
        }
        this.$store.dispatch('setAdminSetting', {});
        resolve(null);
      });
    },
    getShareableURL(user, isCandidate) {
      if (isCandidate == null) {
        isCandidate = user.is_candidate
      }
      return new Promise(async (resolve) => {
        if (!user) {
          resolve("");
        }
        let hashId = user.hash_id;
        if (!hashId) {
          const res = await axios.post("/hash-num", { id: user.id });
          if (res == null || res.data == null) {
            resolve("");
            return;
          }
          hashId = res.data.key;
        }
        resolve(`${this.rootUrl}/${isCandidate ? 'candidates' : 'employers'}/${hashId}`);
      });
    },
    async getShareableDocumentUrl(user, documentId) {
      if (!user) {
        return "";
      }
      const hashId = await this.getUserHashId(user);
      return `${this.rootUrl}/sign-document/${documentId}/${hashId}`
    },
    getMyCandidateShareableUrl(userId) {
      return new Promise(async (resolve) => {
        const hashId = await this.getHashId(userId);
        if (!hashId) {
          resolve('');
          return;
        }
        resolve(`${this.rootUrl}/public-candidates-section/${hashId}`)
      });
    },
    getRecurringAvailabilityShareableUrl(userId) {
      return new Promise(async (resolve) => {
        const hashId = await this.getHashId(userId + 21);
        if (!hashId) {
          resolve('');
          return;
        }
        resolve(`${this.rootUrl}/public-recurring-availability/${userId}/${hashId}`)
      });
    },
    async getUserHashId(user) {
      if (!user) {
        return null;
      }
      if (user.hash_id) {
        return user.hash_id;
      }
      return await this.getHashId(user.id);
    },
    getHashId(id) {
      return new Promise(async (resolve) => {
        const res = await axios.post("/hash-num", {
          id: id,
        });
        if (res == null || res.data == null) {
          resolve(null);
          return;
        }
        resolve(res.data.key);
      });

    },
    // Send request to get total remaining credit at current time
    getCurrentCreditValue(userId) {
      return new Promise(async (resolve, reject) => {
        const res = await axios.get(
          `/get-user-credit/?user=${userId}&check_usable_currently=true`
        );
        let result = 0;
        if (res && res.data && res.data.credits && res.data.credits.length) {
          result = this.calculateSumOfProperty(res.data.credits, 'value');
        }
        resolve(result);
      });
    },
    async addCredits(user, creditNumber, note) {
      return new Promise(async (resolve) => {
        await axios.post('/add-user-credit/', {
          user: user.id,
          user_name: `${user.first_name} ${user.last_name}`,
          value: Number(creditNumber),
          notes: note,
          agency_owner: this.$store.getters.agencyName,
          agency: this.$store.getters.agencyID,
        });
        resolve();
      });
    },
    async removeCredits(user, creditNumber) {
      return new Promise(async (resolve) => {
        await axios.post('/remove-user-credit/', {
          user: user.id,
          user_name: `${user.first_name} ${user.last_name}`,
          value: Number(creditNumber),
          agency_owner: this.$store.getters.agencyName,
          agency: this.$store.getters.agencyID,
        });
        resolve();
      });
    }
  }
});



// WEBPACK FOOTER //
// ./src/mixins/query-handling.js